import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Divider, Modal, Tabs, message } from 'antd';
import { useTranslation } from 'react-i18next';
import ColumnConfigEditor from '../ColumnConfigEditor';
import { updatePartner } from '../../modules/partners/api';

/**
 * OVO-110 — admin-side modal for pre-configuring a partner's default column
 * layout for the Orders and Routes tables that partner viewers see.
 *
 * The column keys MUST stay in sync with the actual `OrdersTable` /
 * `RoutesTable` column definitions. We keep a small parallel meta list here
 * rather than importing the live column arrays — the live arrays carry
 * render functions, widths, sorters etc that we don't need for the visibility
 * + reorder UI and that would force partner-viewer-shaped imports here.
 */

// Keep in sync with `ovo-frontend/src/components/Tables/OrdersTable.jsx` (`allColumns`).
const ORDERS_COLUMNS_META = (t, tc) => [
  { key: 'orderCode', title: t('orderId'), isFixed: true },
  { key: 'orderRef', title: t('orderRef') },
  { key: 'partnerOrderRef', title: t('partnerOrderRef') },
  { key: 'partner', title: t('partner') },
  { key: 'deliveryDate', title: t('deliveryDate') },
  { key: 'name', title: t('deliveryName') },
  { key: 'deliveryAddress', title: t('deliveryAddress') },
  { key: 'routeId', title: t('routeId') },
  { key: 'types', title: t('types') },
  { key: 'createdAt', title: tc('createdAt') },
  { key: 'pickupName', title: t('pickupName') },
  { key: 'pickupAddress', title: t('pickupAddress') },
  { key: 'deliveryPhone', title: t('deliveryPhone') },
  { key: 'pickupTimeWindow', title: t('pickupTimeWindow') },
  { key: 'deliveryTimeWindow', title: t('deliveryTimeWindow') },
  { key: 'volume', title: tc('volume') },
  { key: 'updatedAt', title: tc('updatedAt') },
  { key: 'collectedItems', title: t('empties') },
  { key: 'productTypes', title: t('productType') },
  { key: 'weight', title: t('weight') },
  { key: 'scannedCode', title: t('scanned') },
  { key: 'status', title: tc('status'), isFixed: true },
];

const ORDERS_FIXED_KEYS = ['orderCode', 'status', 'action'];

// Keep in sync with `ovo-frontend/src/components/Tables/RoutesTable.jsx` (`allColumns`).
const ROUTES_COLUMNS_META = (t, tc) => [
  { key: 'id', title: t('routeId') || 'Route ID', isFixed: true },
  { key: 'date', title: t('date') || tc('date') || 'Date' },
  { key: 'driverName', title: t('driver') || 'Driver' },
  { key: 'startTime', title: t('startTime') || 'Start Time' },
  { key: 'endTime', title: t('endTime') || 'End Time' },
  { key: 'duration', title: t('duration') || 'Duration' },
  { key: 'stops', title: t('stops') || 'Stops' },
  { key: 'orders', title: t('orders') || 'Orders' },
  { key: 'partners', title: t('partners') || 'Partners' },
  { key: 'collected', title: t('collected') || 'Collected' },
  { key: 'distance', title: t('distance') || 'Distance' },
  { key: 'travelTime', title: t('travelTime') || 'Travel' },
  { key: 'workingTime', title: t('workingTime') || 'Working' },
  { key: 'serviceTime', title: t('serviceTime') || 'Service' },
  { key: 'workingPercent', title: t('workingPercent') || 'Working %' },
  { key: 'status', title: tc('status'), isFixed: true },
];

const ROUTES_FIXED_KEYS = ['id', 'status', 'action'];

function blankFor(meta) {
  return { hidden: [], order: meta.map((c) => c.key), pageSize: 15 };
}

function PerTableEditor({ meta, fixedKeys, value, onChange, droppableId }) {
  // `columns` is the props-shape that ColumnConfigEditor expects (objects
  // with `key` + `title`). We respect the saved `order` first, then append
  // any unknown columns (new column added to the table after admin saved).
  const orderedColumns = useMemo(() => {
    const byKey = new Map(meta.map((c) => [c.key, c]));
    const out = [];
    for (const k of value.order) {
      if (byKey.has(k)) {
        out.push(byKey.get(k));
        byKey.delete(k);
      }
    }
    for (const [, c] of byKey) out.push(c);
    // Pin fixed-right columns (status) to the end visually. The order array
    // can keep them wherever — `useColumnConfig.orderedAllColumns` re-pins
    // at render time anyway.
    const main = out.filter((c) => c.key !== 'action' && c.key !== 'status');
    const right = out.filter((c) => c.key === 'status' || c.key === 'action');
    return [...main, ...right];
  }, [meta, value.order]);

  const hidden = useMemo(() => new Set(value.hidden), [value.hidden]);

  const handleToggle = (key) => {
    const set = new Set(value.hidden);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    onChange({ ...value, hidden: Array.from(set) });
  };

  const handleMove = (fromIndex, toIndex) => {
    // Build canonical order (same shape orderedAllColumns uses) and splice.
    const orderKeys = orderedColumns.map((c) => c.key);
    const [moved] = orderKeys.splice(fromIndex, 1);
    orderKeys.splice(toIndex, 0, moved);
    onChange({ ...value, order: orderKeys });
  };

  const handlePageSize = (size) => onChange({ ...value, pageSize: size });

  return (
    <ColumnConfigEditor
      columns={orderedColumns}
      hidden={hidden}
      fixedKeys={fixedKeys}
      onToggle={handleToggle}
      onMove={handleMove}
      pageSize={value.pageSize}
      onPageSizeChange={handlePageSize}
      droppableId={droppableId}
    />
  );
}

export default function PartnerColumnDefaultsModal({ open, onClose, partnerId, initialValue, onSaved }) {
  const { t: tOrders } = useTranslation('orders');
  const { t: tRoutes } = useTranslation('routes');
  const { t: tc } = useTranslation('common');

  const ordersMeta = useMemo(() => ORDERS_COLUMNS_META(tOrders, tc), [tOrders, tc]);
  const routesMeta = useMemo(() => ROUTES_COLUMNS_META(tRoutes, tc), [tRoutes, tc]);

  const [orders, setOrders] = useState(() => initialValue?.orders || blankFor(ordersMeta));
  const [routes, setRoutes] = useState(() => initialValue?.routes || blankFor(routesMeta));
  const [saving, setSaving] = useState(false);

  // Re-seed when modal re-opens for a different partner.
  useEffect(() => {
    if (open) {
      setOrders(initialValue?.orders || blankFor(ordersMeta));
      setRoutes(initialValue?.routes || blankFor(routesMeta));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partnerId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { response } = await updatePartner({
        id: partnerId,
        defaultColumns: { orders, routes },
      });
      if (response) {
        message.success('Default columns saved');
        onSaved?.(response);
        onClose();
      } else {
        message.error('Failed to save defaults');
      }
    } catch (e) {
      message.error(e?.message || 'Failed to save defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = async () => {
    setSaving(true);
    try {
      const { response } = await updatePartner({ id: partnerId, defaultColumns: null });
      if (response) {
        message.success('Defaults cleared — partner viewers will use the global default');
        onSaved?.(response);
        onClose();
      }
    } catch (e) {
      message.error(e?.message || 'Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Default columns for partner viewers"
      open={open}
      onCancel={onClose}
      width={520}
      footer={[
        <Button key="clear" danger onClick={handleClearAll} loading={saving} style={{ marginRight: 'auto' }}>
          Clear defaults
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="What partner viewers see on first visit"
        description="Pick which columns are visible (and their order) for the Orders and Routes tables on the partner-viewer dashboard. Viewers can still override per-browser via the gear icon, but Reset to defaults will fall back to what you set here."
      />
      <Tabs
        items={[
          {
            key: 'orders',
            label: 'Orders table',
            children: (
              <PerTableEditor
                meta={ordersMeta}
                fixedKeys={ORDERS_FIXED_KEYS}
                value={orders}
                onChange={setOrders}
                droppableId="partner-defaults-orders"
              />
            ),
          },
          {
            key: 'routes',
            label: 'Routes table',
            children: (
              <PerTableEditor
                meta={routesMeta}
                fixedKeys={ROUTES_FIXED_KEYS}
                value={routes}
                onChange={setRoutes}
                droppableId="partner-defaults-routes"
              />
            ),
          },
        ]}
      />
      <Divider style={{ margin: '12px 0 0' }} />
    </Modal>
  );
}
